import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCamera,
  faCheckCircle,
  faCog,
  faCrown,
  faEdit,
  faEnvelope,
  faGlobe,
  faImage,
  faMapMarkerAlt,
  faPhone,
  faSave,
  faStore,
  faTimes,
  faTimesCircle,
  faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import { useSalonStore } from './useSalonStore';
import WorkingHours from '../../components/working-hours/WorkingHours';
import { useCurrentSalonStore } from '@stores/useCurrentSalonStore.ts';

const SalonDetail: React.FC = () => {
  const { currentSalon } = useCurrentSalonStore();
  const { salonDetail, workingHours, loading, error, selectSalon, clearSelectedSalon, loadSalons, loadSalonDetail, loadWorkingHours, updateWorkingHours, updateSalon } =
    useSalonStore();
  const [isEditing, setIsEditing] = useState(false);
  const [salonData, setSalonData] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    email: '',
    website: '',
    description: '',
  });

  useEffect(() => {
    const loadData = async () => {
      await loadSalons();
      if (currentSalon?.salonId) {
        selectSalon(currentSalon.salonId);
        await loadSalonDetail(currentSalon.salonId);
        await loadWorkingHours(currentSalon.salonId);
      }
    };
    loadData();
    return () => clearSelectedSalon();
  }, [currentSalon?.salonId, selectSalon, clearSelectedSalon, loadSalons, loadSalonDetail, loadWorkingHours]);

  useEffect(() => {
    if (salonDetail) {
      setSalonData({
        name: salonDetail.name,
        address: salonDetail.address,
        city: salonDetail.city,
        postalCode: salonDetail.postalCode,
        phone: salonDetail.phoneNumber ?? '',
        email: salonDetail.email ?? '',
        website: salonDetail.website || '',
        description: salonDetail.description ?? '',
      });
    }
  }, [salonDetail]);

  const handleInputChange = (field: string, value: string) => {
    setSalonData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!salonDetail?.id) return;

    try {
      await updateSalon(salonDetail.id, {
        name: salonData.name,
        address: salonData.address,
        city: salonData.city || '',
        postalCode: salonData.postalCode || '',
        phoneNumber: salonData.phone,
        email: salonData.email || undefined,
        website: salonData.website || undefined,
        description: salonData.description,
        timezone: salonDetail.timezone || 'Europe/Prague',
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Chyba při ukládání salonu:', error);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role?.toUpperCase()) {
      case 'OWNER':
        return 'bg-yellow-100 text-yellow-800';
      case 'RECEPTION':
        return 'bg-blue-100 text-blue-800';
      case 'WORKER':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role?.toUpperCase()) {
      case 'OWNER':
        return faCrown;
      case 'RECEPTION':
        return faCog;
      case 'WORKER':
        return faUserTie;
      default:
        return faUserTie;
    }
  };

  const getRoleLabel = (role?: string) => {
    switch (role?.toUpperCase()) {
      case 'OWNER':
        return 'Vlastník';
      case 'RECEPTION':
        return 'Recepce';
      case 'WORKER':
        return 'Pracovník';
      default:
        return role || '';
    }
  };

  const translatePermission = (permission: string) => {
    const translations: Record<string, string> = {
      MANAGE_SALON: 'Spravovat salon',
      MANAGE_SERVICES: 'Spravovat služby',
      MANAGE_BOOKINGS: 'Spravovat rezervace',
      MANAGE_STAFF: 'Spravovat zaměstnance',
      VIEW_REPORTS: 'Zobrazit reporty',
      MANAGE_SCHEDULE: 'Spravovat rozvrh',
    };
    return translations[permission] || permission;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Načítání salonu...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Chyba při načítání salonu: {error}</p>
        </div>
      </div>
    );
  }

  if (!currentSalon) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faStore} className="text-gray-300 text-6xl mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Salon nenalezen</h3>
          <p className="text-gray-500 mb-4">Požadovaný salon nebyl nalezen nebo k němu nemáte přístup.</p>
          <Link to="/app/salon-profile" className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Zpět na seznam salonů
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link to="/app/salon-profile" className="mr-4 p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{salonDetail?.name || 'Načítání...'}</h1>
            <p className="text-gray-600 mt-1">Detail a nastavení salonu</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Salon Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faImage} className="w-10 h-10 text-gray-400" />
                </div>
                <div className="flex-1">
                  {isEditing ? (
                    <input
                      type="text"
                      value={salonData.name}
                      onChange={e => handleInputChange('name', e.target.value)}
                      className="text-2xl font-semibold text-gray-900 bg-transparent border-b-2 border-gray-300 focus:border-green-500 focus:outline-none w-full"
                    />
                  ) : (
                    <h2 className="text-2xl font-semibold text-gray-900">{salonDetail?.name || 'Načítání...'}</h2>
                  )}
                  <p className="text-gray-600 mt-1">Profesionální kadeřnický salon</p>
                </div>
                {isEditing && (
                  <button className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                    <FontAwesomeIcon icon={faCamera} />
                  </button>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-1" />
                    Upravit salon
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="flex items-center px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                    >
                      <FontAwesomeIcon icon={faTimes} className="mr-1" />
                      Zrušit
                    </button>
                    <button
                      onClick={handleSave}
                      className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <FontAwesomeIcon icon={faSave} className="mr-1" />
                      Uložit salon
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
                  Adresa
                </label>
                {isEditing ? (
                  <textarea
                    rows={3}
                    value={salonData.address}
                    onChange={e => handleInputChange('address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  />
                ) : (
                  <p className="text-gray-900">{salonDetail?.address || 'Načítání...'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faPhone} className="mr-2" />
                  Telefon
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={salonData.phone}
                    onChange={e => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  />
                ) : (
                  <p className="text-gray-900">{salonData.phone || 'Není nastaveno'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                  E-mail
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    value={salonData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  />
                ) : (
                  <p className="text-gray-900">{salonData.email || 'Není nastaveno'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faGlobe} className="mr-2" />
                  Webová stránka
                </label>
                {isEditing ? (
                  <input
                    type="url"
                    value={salonData.website}
                    onChange={e => handleInputChange('website', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    placeholder="https://www.example.com"
                  />
                ) : (
                  <p className="text-gray-900">
                    {salonData.website ? (
                      <a href={salonData.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                        {salonData.website}
                      </a>
                    ) : (
                      'Není nastaveno'
                    )}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Popis salonu</label>
              {isEditing ? (
                <textarea
                  rows={4}
                  value={salonData.description}
                  onChange={e => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  placeholder="Popište váš salon..."
                />
              ) : (
                <p className="text-gray-900">{salonData.description || 'Popis není nastaven'}</p>
              )}
            </div>
          </div>

          {/* Working Hours */}
          <WorkingHours
            workingHours={workingHours}
            onSave={async data => {
              if (salonDetail?.id) {
                await updateWorkingHours(salonDetail.id, data);
              }
            }}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Role and Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Vaše oprávnění</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Role</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center ${getRoleColor(currentSalon?.role || '')}`}>
                  <FontAwesomeIcon icon={getRoleIcon(currentSalon?.role || '')} className="mr-1" />
                  {getRoleLabel(currentSalon?.role)}
                </span>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Oprávnění</h3>
            <div className="space-y-2">
              {currentSalon?.permissions?.map(permission => (
                <div key={permission} className="flex items-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2" />
                  <span className="text-sm text-gray-700">{translatePermission(permission)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Capabilities */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Možnosti</h3>
            <div className="space-y-2">
              {currentSalon && Object.entries(currentSalon)
                .filter(([key, value]) => key.startsWith('can') && typeof value === 'boolean')
                .map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      {key
                        .replace('can', '')
                        .replace(/([A-Z])/g, ' $1')
                        .trim()}
                    </span>
                    <FontAwesomeIcon icon={value ? faCheckCircle : faTimesCircle} className={value ? 'text-green-500' : 'text-red-500'} />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalonDetail;
